var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import { gql } from '@apollo/client';
import { DateTime } from 'luxon';
import { areDatesEqual, datesRemainingInInterval, DayOfWeekMappings, getWeekIntervalDates } from '@amzn/atoz-shiftapp-utilities';
import { completedSwapsToDaysMap as generateCompletedSwapsToDaysMap, pendingSwapsToDaysMap as generatePendingSwapsToDaysMap } from '../../../../utils';
export var weekHasCompletedSwap = function (completedSwapsToDaysMap, shiftStartTime) {
    return Object.keys(completedSwapsToDaysMap).some(function (completedSwapDay) {
        var _a = getWeekIntervalDates(DateTime.fromISO(completedSwapDay)), weekStart = _a.weekStart, weekEnd = _a.weekEnd;
        return shiftStartTime >= weekStart && shiftStartTime <= weekEnd;
    });
};
export var weekHasDayWithoutShift = function (daysWithShift, shiftWeekInterval, swapRequestBoundariesStart) {
    var scheduledDaysWithShiftInCurrentWeek = daysWithShift.filter(function (day) {
        return shiftWeekInterval.contains(day);
    });
    var daysLeftInCurrentWeek = datesRemainingInInterval(swapRequestBoundariesStart, shiftWeekInterval);
    var remainingDaysWithoutShifts = daysLeftInCurrentWeek.filter(function (day) {
        return !scheduledDaysWithShiftInCurrentWeek.some(function (scheduledDate) {
            return areDatesEqual(day, scheduledDate);
        });
    });
    return !!(remainingDaysWithoutShifts === null || remainingDaysWithoutShifts === void 0 ? void 0 : remainingDaysWithoutShifts.length);
};
export var isShiftWithinSwapBoundaries = function (shiftStartDateTime, swapRequestBoundaries) {
    if (!swapRequestBoundaries) {
        return true;
    }
    else {
        return (shiftStartDateTime >
            DateTime.fromISO(swapRequestBoundaries.start) &&
            shiftStartDateTime < DateTime.fromISO(swapRequestBoundaries.end));
    }
};
export var isSwapRestricted = function (shiftStartDateTime, completedSwapsToDaysMap, swapOutRestriction) {
    if (!swapOutRestriction) {
        return false;
    }
    if (!swapOutRestriction.restrictedDaysOfWeek.includes(DayOfWeekMappings[shiftStartDateTime.weekdayShort])) {
        return false;
    }
    var restrictionWindowStart = getWeekIntervalDates(shiftStartDateTime.minus({
        weeks: swapOutRestriction.restrictionForNumberOfWeeks
    })).weekStart;
    var restrictionWindowEnd = getWeekIntervalDates(shiftStartDateTime.plus({
        weeks: swapOutRestriction.restrictionForNumberOfWeeks
    })).weekEnd;
    return Object.keys(completedSwapsToDaysMap).some(function (completedSwapDay) {
        var swapWorkdayDate = DateTime.fromISO(completedSwapDay);
        return (swapOutRestriction.restrictedDaysOfWeek.includes(DayOfWeekMappings[swapWorkdayDate.weekdayShort]) &&
            restrictionWindowStart <= swapWorkdayDate &&
            restrictionWindowEnd >= swapWorkdayDate);
    });
};
var bothButtonsFalse = {
    showSwapRequestButton: false,
    showSwapCancelButton: false
};
export var useSwapButtons = function (_a) {
    var _b;
    var swaps = _a.swaps, scheduleShift = _a.scheduleShift, siteSwapConfiguration = _a.siteSwapConfiguration, daysWithShift = _a.daysWithShift;
    var _c = siteSwapConfiguration || {}, swapEnabled = _c.swapEnabled, swapRequestBoundaries = _c.swapRequestBoundaries, blockedDates = _c.blockedDates, swapOutRestriction = _c.swapOutRestriction;
    if (!swaps || !scheduleShift || !swapEnabled) {
        return bothButtonsFalse;
    }
    var shiftStartTime = scheduleShift.shift.timeRange.start;
    var completedSwapsToDaysMap = generateCompletedSwapsToDaysMap(swaps.swaps);
    var pendingSwapsToDaysMap = generatePendingSwapsToDaysMap(swaps.swaps);
    var shiftStartDateTime = DateTime.fromISO(shiftStartTime);
    var hasPendingSwaps = !!((_b = pendingSwapsToDaysMap[shiftStartDateTime.toISODate()]) === null || _b === void 0 ? void 0 : _b.length);
    if (hasPendingSwaps) {
        return {
            showSwapRequestButton: false,
            showSwapCancelButton: true
        };
    }
    var isWithinSwapRequestBoundaries = isShiftWithinSwapBoundaries(shiftStartDateTime, swapRequestBoundaries);
    if (!isWithinSwapRequestBoundaries) {
        return bothButtonsFalse;
    }
    var doesWeekHasCompletedSwaps = weekHasCompletedSwap(completedSwapsToDaysMap, shiftStartDateTime);
    if (doesWeekHasCompletedSwaps) {
        return bothButtonsFalse;
    }
    var isDayBlocked;
    if (!blockedDates || !blockedDates.length) {
        isDayBlocked = false;
    }
    else {
        isDayBlocked = blockedDates.some(function (blockedDate) { return blockedDate === shiftStartDateTime.toISODate(); });
    }
    if (isDayBlocked) {
        return bothButtonsFalse;
    }
    var isDayRestricted = isSwapRestricted(shiftStartDateTime, completedSwapsToDaysMap, swapOutRestriction);
    if (isDayRestricted) {
        return bothButtonsFalse;
    }
    var today = DateTime.local();
    var weekInterval = getWeekIntervalDates(today).weekInterval;
    var isShiftInCurrentWeek = weekInterval.contains(shiftStartDateTime);
    // If there are only future working days in the same week as now, do not display the swap button
    var daysAvailableToSwapInWeek;
    if (!isShiftInCurrentWeek) {
        daysAvailableToSwapInWeek = true;
    }
    else {
        daysAvailableToSwapInWeek = weekHasDayWithoutShift(daysWithShift, weekInterval, swapRequestBoundaries
            ? DateTime.fromISO(swapRequestBoundaries.start)
            : today);
    }
    if (!daysAvailableToSwapInWeek) {
        return bothButtonsFalse;
    }
    // If it reaches here, showSwapRequestButton = true, showSwapCancelButton = false
    // using variables flags to make it more clear why its like that
    return {
        showSwapRequestButton: !isDayBlocked &&
            isWithinSwapRequestBoundaries &&
            !hasPendingSwaps &&
            !doesWeekHasCompletedSwaps &&
            !isDayRestricted &&
            daysAvailableToSwapInWeek,
        showSwapCancelButton: hasPendingSwaps
    };
};
useSwapButtons.fragments = {
    siteSwapConfiguration: gql(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n        fragment UseSwapButtons_SiteSwapConfiguration on SiteSwapConfiguration {\n            swapEnabled\n            swapRequestBoundaries {\n                start\n                end\n            }\n            blockedDates\n            swapOutRestriction {\n                restrictedDaysOfWeek\n                restrictionForNumberOfWeeks\n            }\n        }\n    "], ["\n        fragment UseSwapButtons_SiteSwapConfiguration on SiteSwapConfiguration {\n            swapEnabled\n            swapRequestBoundaries {\n                start\n                end\n            }\n            blockedDates\n            swapOutRestriction {\n                restrictedDaysOfWeek\n                restrictionForNumberOfWeeks\n            }\n        }\n    "]))),
    swaps: gql(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n        fragment UseSwapButtons_Swaps on Swaps {\n            swaps {\n                id\n                swapFrom\n                swapTo\n                status\n            }\n        }\n    "], ["\n        fragment UseSwapButtons_Swaps on Swaps {\n            swaps {\n                id\n                swapFrom\n                swapTo\n                status\n            }\n        }\n    "]))),
    scheduleShift: gql(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n        fragment UseSwapButtons_ScheduleShift on ScheduleShift {\n            shift {\n                timeRange {\n                    start\n                }\n            }\n        }\n    "], ["\n        fragment UseSwapButtons_ScheduleShift on ScheduleShift {\n            shift {\n                timeRange {\n                    start\n                }\n            }\n        }\n    "])))
};
var templateObject_1, templateObject_2, templateObject_3;
